import React, { Fragment, useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select'
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'
import Button from "../../../Button";
import makeAnimated from 'react-select/animated';
import styles from './StudentImport.module.sass'
import ClipLoader from 'react-spinners/ClipLoader';
import ImportPreview from './ImportPreview';
import { findHeaderMatch, FIELD_LABELS } from '../../../../utils/headerMatch';

const HeaderMatcher = ({ formData, school, headers, closeModal }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [studentImport, setStudentImport] = useState({});
  const [comparison, setComparison] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);
  const [previewAcknowledged, setPreviewAcknowledged] = useState(false);

  const { register, handleSubmit, control } = useForm();
  const animatedComponents = makeAnimated();
  const csvHeaders = FIELD_LABELS;

  // Staff files have no grade column — every row is forced to 'ST' server-side.
  const isStaffFile = formData && formData.get('staff_file') === 'on'
  const requiredFields = isStaffFile ? ['first_name', 'last_name'] : ['first_name', 'last_name', 'grade']

  const onSubmit = (data) => {
    setIsSubmitting(true);

    formData.append('headers', JSON.stringify(data));
    formData.append('school_id', school.value);

    fetch('/admin/student_imports',
      { method: 'POST', body: formData })
      .then(res => res.json())
      .then(json => {
        setStudentImport(json);
        // Nothing has been written yet - the roster is only imported once the
        // operator OKs the year-over-year preview below.
        fetchComparison(json.id);
      })
      .catch((error) => {
        toast('There was an error processing the CSV file. Please try again or use a different file.', { type: 'error' })
        setIsSubmitting(false)
      })
  }

  const fetchComparison = (id) => {
    setComparisonLoading(true);

    fetch(`/admin/student_imports/${id}/prior_season_comparison`)
      .then((res) => res.json())
      .then((json) => {
        if (json.error) setComparisonError(json.error);
        setComparison(json);
        setComparisonLoading(false);
      })
      .catch(() => {
        setComparisonError('Could not compare this file to last season. Review the file before importing.');
        setComparisonLoading(false);
      })
  }

  // The preview is a review step, not a gate: once acknowledged the flow falls
  // through to the existing blocker/warning screens exactly as before.
  const handlePreviewContinue = () => {
    setPreviewAcknowledged(true);

    const hasBlockers = studentImport.import_blockers && studentImport.import_blockers.length > 0;

    if (!studentImport.overwrite_warning && !studentImport.teacher_missing_error
      && !studentImport.teacher_reassignment_warning && !hasBlockers) {
      handleContinue(false, studentImport.id)
    }
  }

  const handleContinue = (unenroll, id = undefined, force = false) => {
    setIsSubmitting(true)

    fetch(`/admin/student_imports/${id || studentImport.id}/import_csv?unenroll=${unenroll}&force=${force}`)
      .then(() => {
        window.location.href = `/admin/student_imports/${id || studentImport.id}`
      })
      .catch((error) => {
        toast('There was an error processing the CSV file. Please try again or use a different file.', { type: 'error' })
        setIsSubmitting(false)
      })
  }

  const renderHeaderOptions = () => (
    headers.map((header) => ({ label: header, value: header }))
  )

  // The guess lives in utils/headerMatch, derived from the 43,947 header
  // choices operators have already made (student_imports.header_mapping) and
  // tested against them. The rule that was here -- raw case-sensitive
  // `includes` -- auto-matched 66% of those; this matches 97.9%, and the
  // difference is mostly byte-order marks, casing and a curated alias table.
  const findDefaultValue = (key, value) => findHeaderMatch(headers, key, value);

  const renderInputs = () => {
    const keys = Object.keys(csvHeaders);

    if (isSubmitting) {
      return (
        <div className={styles.loadingContainer}>
          <p>Importing student CSV for {school.label}. Please wait until you're redirected.</p>
          <ClipLoader color={'blue'} loading={true} size={150} />
        </div>
      )
    }

    return (
      keys.map((key, i) => {
        return (
          <label key={key}>
            {csvHeaders[key]}
            <Controller
              control={control}
              defaultValue={findDefaultValue(key, csvHeaders[key])}
              name={key}
              render={({ field: { onChange, value } }) => (
                <Select
                  {...register(key, { required: requiredFields.includes(key) })}
                  menuPlacement={i > 3 ? 'top' : 'bottom'}
                  onChange={({ value: newValue }) => onChange({ label: newValue, value: newValue })}
                  className="basic-single"
                  components={animatedComponents}
                  defaultValue={findDefaultValue(key, csvHeaders[key])}
                  options={renderHeaderOptions()}
                  name={key}
                />
              )}
            />
          </label>
        )
      })
    )
  }

  if (studentImport.id && !previewAcknowledged) {
    return (
      <ImportPreview
        comparison={comparison}
        loading={comparisonLoading}
        error={comparisonError}
        onCancel={closeModal}
        onContinue={handlePreviewContinue}
      />
    )
  }

  if (studentImport.import_blockers && studentImport.import_blockers.length > 0) {
    return (
      <>
        <p className={styles.warning}>
          This file looks like it may have roster errors. Importing it could overwrite existing
          students with another student&apos;s data. Please review before proceeding:
        </p>

        <ul className={styles.warning}>
          {studentImport.import_blockers.map((blocker) => (
            <li key={blocker}>{blocker}</li>
          ))}
        </ul>

        <div className={styles.buttonContainer}>
          <a
            type="button"
            className={`btn btn-danger ${styles.btn}`}
            onClick={() => handleContinue(false, undefined, true)}
          >
            I have reviewed these — import anyway
          </a>
        </div>
      </>
    )
  }

  if (studentImport.teacher_missing_error) {
    return (
      <>
        <div className={styles.buttonContainer}>
          <a type="button" className={`btn btn-primary ${styles.btn}`} onClick={() => handleContinue(false)}>
            Continue anyway
          </a>
        </div>

        <p className={styles.warning}>
          {studentImport.teacher_missing_error}
        </p>
      </>
    )
  }

  if (studentImport.teacher_reassignment_warning) {
    return (
      <>
        <p className={styles.warning}>
          {studentImport.teacher_reassignment_warning}
        </p>

        <div className={styles.buttonContainer}>
          <a type="button" className={`btn btn-primary ${styles.btn}`} onClick={() => handleContinue(false)}>
            Continue and use this file&apos;s teachers
          </a>
        </div>
      </>
    )
  }

  if (studentImport.overwrite_warning) {
    return (
      <>
        <p className={styles.warning}>
          {studentImport.overwrite_warning}
        </p>

        <div className={styles.buttonContainer}>
          <a type="button" className={`btn btn-primary ${styles.btn}`} onClick={() => handleContinue(false)}>
            No, keep existing students and process this CSV
          </a>

          <a type="button" className={`btn btn-danger ${styles.btn}`} onClick={() => handleContinue(true)}>
            Yes, unenroll all students that are not present in this CSV
          </a>
        </div>
      </>
    )
  }


  return (
    <Fragment>
      {!isSubmitting && (
        <p className={styles.importInstructions}>
          Match headers to import {isStaffFile ? 'STAFF' : 'students'} into {school.label}
          {isStaffFile && ' — every row will be imported with grade ST'}
        </p>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        {renderInputs()}

        {!isSubmitting && (
          <Button
            fullWidth
            classes={styles.button}
            variant='primary'
            disabled={isSubmitting}
          >
            Continue
          </Button>
        )}
      </form>
    </Fragment>
  )
}

export default HeaderMatcher;
